import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const inputCls =
    "bg-white border border-[#0D1B2A]/20 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30 rounded-xl px-4 py-3 text-[#0D1B2A] placeholder:text-gray-400 outline-none w-full transition-all text-sm";

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Use it on your next login.");
      onOpenChange(false);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not change password"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F8F6F0] border-[#C5A059]/30 max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#0A192F] flex items-center gap-2">
            <ShieldCheck size={22} className="text-[#C5A059]" /> Change Password
          </DialogTitle>
          <DialogDescription className="text-[#4A5568] text-sm">
            Your new password takes effect immediately. Keep it somewhere safe — only you can change it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="change-password-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#0D1B2A]/60 block mb-1.5">Current password</label>
            <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} data-testid="current-password-input" autoComplete="current-password" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#0D1B2A]/60 block mb-1.5">New password (min 8 characters)</label>
            <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className={inputCls} data-testid="new-password-input" autoComplete="new-password" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#0D1B2A]/60 block mb-1.5">Confirm new password</label>
            <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="confirm-password-input" autoComplete="new-password" />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="w-full bg-[#0A192F] text-[#F8F6F0] hover:bg-[#C5A059] hover:text-[#0A192F] rounded-full py-3.5 font-semibold transition-all duration-300 disabled:opacity-60"
            data-testid="change-password-submit"
          >
            {saving ? "Updating…" : "Update Password"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
